import { useEffect, useState } from "react";
import { billingApi, plansApi, type Plan, type Subscription, ApiError } from "@/lib/api";

function formatPrice(price: string): string {
  const n = parseFloat(price);
  return `R ${n.toLocaleString("en-ZA", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" });
}

const STATUS_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  trialing: { bg: "bg-primary-50", text: "text-primary-700", label: "Trial" },
  active: { bg: "bg-secondary-50", text: "text-secondary-700", label: "Active" },
  past_due: { bg: "bg-accent-50", text: "text-accent-700", label: "Payment Due" },
  cancelled: { bg: "bg-foreground-100", text: "text-foreground-600", label: "Cancelled" },
};

function submitToPayFast(action: string, fields: Record<string, string | number>) {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = action;

  for (const [key, value] of Object.entries(fields)) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = key;
    input.value = String(value);
    form.appendChild(input);
  }

  document.body.appendChild(form);
  form.submit();
}

export default function BillingPage() {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [checkingOutPlanId, setCheckingOutPlanId] = useState<number | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([billingApi.subscription(), plansApi.list()])
      .then(([subRes, plansRes]) => {
        setSubscription(subRes.data);
        setPlans(plansRes.data);
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : "Failed to load billing info"))
      .finally(() => setLoading(false));
  }, []);

  const handleSubscribe = async (planId: number) => {
    setCheckingOutPlanId(planId);
    setError("");
    try {
      const res = await billingApi.checkout(planId);
      submitToPayFast(res.data.action, res.data.fields);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to start checkout");
      setCheckingOutPlanId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-foreground-400 text-sm">
        <i className="ri-loader-4-line animate-spin mr-2"></i>Loading...
      </div>
    );
  }

  const st = subscription ? STATUS_STYLES[subscription.status] ?? STATUS_STYLES.active : null;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-primary-600 text-xl md:text-2xl font-bold">Billing</h1>
        <p className="text-foreground-500 text-sm mt-1">Manage your SupplyGuard subscription.</p>
      </div>

      {error && (
        <div className="flex items-start gap-2.5 bg-accent-50 border border-accent-200 rounded-lg px-3.5 py-3 mb-6">
          <i className="ri-error-warning-line text-accent-600 text-sm mt-0.5 shrink-0"></i>
          <p className="text-accent-700 text-sm">{error}</p>
        </div>
      )}

      {/* Current plan */}
      <div className="bg-white rounded-lg border border-background-200 p-4 md:p-5 mb-6">
        <p className="text-primary-600 text-base font-semibold mb-4">Current Plan</p>
        {subscription ? (
          <div className="flex flex-wrap items-center gap-4">
            <div>
              <p className="text-foreground-900 text-xl font-bold">{subscription.plan?.name ?? "—"}</p>
              <p className="text-foreground-500 text-sm mt-0.5">
                {subscription.status === "trialing"
                  ? `Trial ends ${formatDate(subscription.trial_ends_at)}`
                  : subscription.current_period_end
                  ? `Renews ${formatDate(subscription.current_period_end)}`
                  : "No expiry"}
              </p>
            </div>
            {st && (
              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${st.bg} ${st.text}`}>
                {st.label}
              </span>
            )}
          </div>
        ) : (
          <p className="text-foreground-400 text-sm">No subscription on file.</p>
        )}
      </div>

      {/* Available plans */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {plans.map((plan) => {
          const isCurrent = subscription?.plan_id === plan.id && subscription?.status === "active";
          return (
            <div key={plan.id} className="bg-white rounded-lg border border-background-200 p-5 flex flex-col">
              <p className="text-foreground-500 text-sm font-semibold">{plan.name}</p>
              <p className="text-foreground-900 text-2xl font-bold mt-1">
                {formatPrice(plan.price)} <span className="text-foreground-400 text-sm font-medium">/mo</span>
              </p>
              <ul className="mt-4 space-y-2 flex-1">
                <li className="flex items-center gap-2 text-xs text-foreground-600">
                  <i className="ri-check-line text-secondary-500"></i>
                  {plan.max_contracts ? `Up to ${plan.max_contracts} contracts` : "Unlimited contracts"}
                </li>
                <li className="flex items-center gap-2 text-xs text-foreground-600">
                  <i className="ri-check-line text-secondary-500"></i>
                  {plan.max_users ? `Up to ${plan.max_users} users` : "Unlimited users"}
                </li>
                <li className="flex items-center gap-2 text-xs text-foreground-600">
                  <i className={`${plan.has_workflow_engine ? "ri-check-line text-secondary-500" : "ri-close-line text-foreground-300"}`}></i>
                  Renewal Workflow &amp; SLA Matrix
                </li>
              </ul>
              <button
                onClick={() => handleSubscribe(plan.id)}
                disabled={isCurrent || checkingOutPlanId !== null}
                className="mt-5 h-10 bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer flex items-center justify-center gap-2"
              >
                {checkingOutPlanId === plan.id ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-sm"></i>
                    <span>Redirecting…</span>
                  </>
                ) : isCurrent ? (
                  "Current Plan"
                ) : (
                  "Subscribe"
                )}
              </button>
            </div>
          );
        })}
      </div>

      <p className="text-foreground-400 text-xs mt-6">
        <i className="ri-shield-check-line mr-1"></i>
        Payments are processed securely via PayFast (sandbox mode — no real charges).
      </p>
    </div>
  );
}
